import React, { useState, useEffect } from "react";
import SimpleBar from "simplebar-react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSignOutAlt,
  faTimes,
  faBars,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { Image, Navbar } from "@themesberg/react-bootstrap";
import { Link } from "react-router-dom";
import { Routes } from "../routes";
import ReactHero from "../assets/img/state-govt-logo.png";
import ProfilePicture from "../assets/img/default_avatar.png";
import { useSelector } from "react-redux";
import { mainTreasuryModules } from "../pages/examples/user-admin/modules";

/* ─── Design tokens ─── */
const COLORS = {
  bg:         "#099019",
  bgHover:    "rgba(255,255,255,0.07)",
  bgActive:   "#1d4ed8",
  submenuBg:  "rgba(0,0,0,0.2)",
  text:       "#fff",
  textActive: "#ffffff",
  textMuted:  "#fff",
  border:     "rgba(255,255,255,0.08)",
  accent:     "#3b82f6",
};

/* ─── Collapsible menu group ─── */
const CollapsableNavItem = ({
  eventKey, title, icon, children,
  openDropdowns, setOpenDropdowns, onNavClick,
}) => {
  const location = useLocation();
  const isOpen   = openDropdowns.includes(eventKey);

  // Auto-expand if current path lives inside this group
  useEffect(() => {
    if (location.pathname.includes(eventKey)) {
      setOpenDropdowns((p) => (p.includes(eventKey) ? p : [...p, eventKey]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () =>
    setOpenDropdowns((p) =>
      p.includes(eventKey) ? p.filter((k) => k !== eventKey) : [eventKey]
    );

  return (
    <div>
      {/* Group header */}
      <div
        onClick={toggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "9px 12px",
          borderRadius: 8,
          cursor: "pointer",
          color: isOpen ? COLORS.textActive : COLORS.text,
          backgroundColor: isOpen ? COLORS.bgHover : "transparent",
          transition: "background 0.15s",
          userSelect: "none",
          marginBottom: 2,
        }}
        onMouseEnter={(e) => { if (!isOpen) e.currentTarget.style.backgroundColor = COLORS.bgHover; }}
        onMouseLeave={(e) => { if (!isOpen) e.currentTarget.style.backgroundColor = "transparent"; }}
      >
        <span style={{ width: 28, textAlign: "center", fontSize: 15, color: isOpen ? COLORS.accent : COLORS.textMuted }}>
          <FontAwesomeIcon icon={icon} />
        </span>
        <span style={{ flex: 1, fontSize: 15, fontWeight: 500, lineHeight: 1.3 }}>{title}</span>
        <FontAwesomeIcon
          icon={isOpen ? faChevronDown : faChevronRight}
          style={{ fontSize: 9, color: COLORS.textMuted }}
        />
      </div>

      {/* Sub-items */}
      {isOpen && (
        <div style={{
          marginLeft: 14,
          paddingLeft: 8,
          borderLeft: `2px solid ${COLORS.border}`,
          marginBottom: 4,
        }}>
          {React.Children.map(children, (child) =>
            child ? React.cloneElement(child, { onNavClick }) : null
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Single nav item ─── */
const NavItem = ({ title, link, icon, onNavClick }) => {
  const location = useLocation();
  const history  = useNavigate();
  const isActive = location.pathname === link || location.pathname.startsWith(link + "?");

  return (
    <div
      onClick={() => { history(link); if (onNavClick) onNavClick(); }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        borderRadius: 7,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: isActive ? 600 : 400,
        color: isActive ? COLORS.textActive : COLORS.text,
        backgroundColor: isActive ? COLORS.bgActive : "transparent",
        transition: "background 0.15s",
        marginBottom: 2,
      }}
      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = COLORS.bgHover; }}
      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = isActive ? COLORS.bgActive : "transparent"; }}
    >
      <span style={{ width: 24, textAlign: "center", fontSize: 15, color: isActive ? "#fff" : COLORS.textMuted }}>
        <FontAwesomeIcon icon={icon} />
      </span>
      <span style={{ flex: 1, lineHeight: 1.3, fontSize: 15 }}>{title}</span>
    </div>
  );
};

/* ─── Shared nav list (renders modules) ─── */
const NavList = ({ user, openDropdowns, setOpenDropdowns, onNavClick }) => {
  const toArray = (v) =>
    Array.isArray(v)
      ? v.map((s) => String(s).trim())
      : typeof v === "string"
      ? v.split(",").map((s) => s.trim()).filter(Boolean)
      : [];

  const accessTo        = toArray(user?.accessTo);
  const functionalities = toArray(user?.functionalities);
  // Case-insensitive check
  const canAccess = (list, title) =>
    list.some((s) => s.toLowerCase() === title.toLowerCase());

  return (
    <div style={{ padding: "8px 8px" }}>
      {mainTreasuryModules.map((module, index) => {
        if (!canAccess(accessTo, module.title)) return null;
        const modKey = module.title.split(" ").join("-");

        if (module.subMenu && module.subMenu.length) {
          const visibleItems = module.subMenu.filter((item) => {
            const isMDA   = item.title === "CC Copies";
            const hasPend = canAccess(functionalities, "Pending Releases");
            return canAccess(functionalities, item.title) || (isMDA && hasPend);
          });
          // If functionalities grants the module but no subMenu items match,
          // show all subMenu items rather than hiding the whole module
          const itemsToRender = visibleItems.length ? visibleItems : module.subMenu;

          return (
            <CollapsableNavItem
              key={index}
              eventKey={modKey}
              title={module.title}
              icon={module.icon}
              openDropdowns={openDropdowns}
              setOpenDropdowns={setOpenDropdowns}
              onNavClick={onNavClick}
            >
              {itemsToRender.map((item, idx) => (
                <NavItem
                  key={`${modKey}-${idx}`}
                  title={item.title}
                  link={item.route}
                  icon={item.icon || module.icon}
                  onNavClick={onNavClick}
                />
              ))}
            </CollapsableNavItem>
          );
        }

        return (
          <NavItem
            key={module.key || index}
            title={module.title}
            link={module.route}
            icon={module.icon}
            onNavClick={onNavClick}
          />
        );
      })}
    </div>
  );
};

/* ─── Main component ─── */
export default () => {
  const location         = useLocation();
  const [show, setShow]  = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState([]);
  const user             = useSelector((state) => state.auth.user);
  const history          = useNavigate();

  // Close on route change
  useEffect(() => { setShow(false); }, [location.pathname]);

  const onCollapse  = () => setShow(!show);
  const closeSidebar = () => setShow(false);

  /* Sidebar inner content — shared between desktop and mobile */
  const SidebarContent = ({ onNavClick }) => (
    <>
      {/* Logo */}
      <div
        onClick={() => history("/")}
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "16px 14px 12px",
          borderBottom: `1px solid ${COLORS.border}`,
          cursor: "pointer", flexShrink: 0,
          // background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
        }}
      >
        <Image
          src={ReactHero}
          style={{ height: 42, width: 42, borderRadius: 10, flexShrink: 0 }}
        />
        <div>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: 14, letterSpacing: 0.4 }}>
            KIFMIS
          </div>
          <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 500 }}>
            Katsina State
          </div>
        </div>
      </div>

      {/* User badge */}
      {/* <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 14px", borderBottom: `1px solid ${COLORS.border}`,
        flexShrink: 0,
      }}>
        <Image
          src={ProfilePicture}
          style={{ width: 32, height: 32, borderRadius: "50%", border: `2px solid ${COLORS.border}`, flexShrink: 0 }}
        />
        <div style={{ minWidth: 0 }}>
          <div style={{ color: "#fff", fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {user?.username || "User"}
          </div>
          <div style={{ color: COLORS.textMuted, fontSize: 10 }}>{user?.role || "Staff"}</div>
        </div>
      </div> */}

      {/* Scrollable nav */}
      {/* <SimpleBar style={{ flex: 1, minHeight: 0 }}> */}
      <div style={{overflow:'scroll'}}>
        <NavList
          user={user}
          openDropdowns={openDropdowns}
          setOpenDropdowns={setOpenDropdowns}
          onNavClick={onNavClick}
        />
        </div>
      {/* </SimpleBar> */}

      {/* Sign out */}
    <div style={{ padding: "8px 8px", borderTop: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
        <div
          onClick={() => { if (onNavClick) onNavClick(); history(Routes.Signin.path); }}
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "9px 12px", borderRadius: 8,
            cursor: "pointer", fontSize: 13,
            color: "#f87171", transition: "background 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.12)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
        >
          <span style={{ width: 24, textAlign: "center" }}>
            <FontAwesomeIcon icon={faSignOutAlt} />
          </span>
          Sign Out
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* ── Mobile top navbar ── */}
      <Navbar
        expand={false}
        collapseOnSelect
        className=" px-3 d-md-none bg-primary"
        style={{ padding: "8px 16px" }}
      >
        <Navbar.Brand as={Link} to={Routes.DashboardOverview.path} className="me-auto">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Image src={ReactHero} style={{ height: 36, width: 36, borderRadius: 8 }} />
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>KIFMIS</span>
          </div>
        </Navbar.Brand>
        <Navbar.Toggle
          onClick={onCollapse}
          aria-controls="main-navbar"
          style={{
            background: "none", border: "1px solid rgba(255,255,255,0.2)",
            color: "#fff", borderRadius: 8, padding: "5px 10px",
          }}
        >
          <FontAwesomeIcon icon={show ? faTimes : faBars} />
        </Navbar.Toggle>
      </Navbar>

      {/* ── Mobile overlay backdrop ── */}
      {show && (
        <div
          onClick={closeSidebar}
          className="d-md-none"
          style={{
            position: "fixed", inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            zIndex: 1044,
          }}
        />
      )}

      {/* ── Desktop sidebar (hidden on mobile) ── */}
      <div
        className="sidebar-v2 d-none d-md-flex"
        style={{
          backgroundColor: COLORS.bg,
          background: "#099019",
          width: 260,
          position: "fixed",
          top: 0, left: 0,
          height: "100vh",
          zIndex: 1045,
          display: "flex",
          flexDirection: "column",
          backgroundImage: "none",
        }}
      >
        <SidebarContent onNavClick={null} />
      </div>

      {/* ── Mobile sidebar drawer ── */}
      <div
        className="d-md-none"
        style={{
          position: "fixed",
          top: 0, left: 0,
          height: "100vh",
          width: show ? 270 : 0,
          backgroundColor: COLORS.bg,
          zIndex: 1045,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transition: "width 0.25s ease",
          boxShadow: show ? "4px 0 20px rgba(0,0,0,0.5)" : "none",
        }}
      >
        {/* Mobile close button row */}
        <div style={{
          display: "flex", justifyContent: "flex-end",
          padding: "10px 12px 0",
          flexShrink: 0,
        }}>
          <button
            onClick={closeSidebar}
            style={{
              background: "none", border: "none",
              color: COLORS.textMuted, cursor: "pointer",
              fontSize: 18, padding: 4,
            }}
            aria-label="Close menu"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <SidebarContent onNavClick={closeSidebar} />
      </div>
    </>
  );
};
